import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/user-agreement")({
  component: UserAgreementPage,
});

function UserAgreementPage() {
  useEffect(() => {
    document.title = "User Agreement — Weborbis Sport";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "User Agreement outlining the rights and responsibilities of Weborbis Sport visitors.");
    }
  }, []);
  return (
    <LegalPage title="User Agreement" updated="July 21, 2026">
      <p>
        This User Agreement ("Agreement") governs your access to and use of the Weborbis Sport
        website located at <strong>weborbis.top</strong> ("the Site"). By accessing or using the
        Site, you agree to be bound by this Agreement, our Terms of Service, and our Privacy
        Policy. If you do not agree with any part of this Agreement, you may not access or use the
        Site.
      </p>

      <h2>1. Acceptance of Agreement</h2>
      <p>
        Your access to and use of the Site is conditioned on your acceptance of and compliance
        with this Agreement. This Agreement applies to all visitors, users, and others who access
        or use the Site.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 13 years of age to use the Site. If you are under the age of
        majority in your jurisdiction, you may use the Site only with the involvement of a parent
        or legal guardian. By using the Site, you represent and warrant that you meet these
        eligibility requirements.
      </p>

      <h2>3. Account Registration (if applicable)</h2>
      <p>
        If you create an account on the Site, you are responsible for maintaining the
        confidentiality of your account credentials and for all activities that occur under your
        account. You agree to notify us immediately of any unauthorized use of your account.
      </p>

      <h2>4. Acceptable Use Policy</h2>
      <p>You agree to use the Site only for lawful purposes and in accordance with this Agreement.</p>
      <ul>
        <li>Do not use the Site for any unlawful, misleading, or fraudulent purpose.</li>
        <li>
          Do not attempt to reverse engineer, decompile, disassemble, or interfere with the
          operation of the Site.
        </li>
        <li>
          Do not upload, transmit, or distribute any material that contains viruses, malware, or
          other malicious code.
        </li>
        <li>
          Do not use automated systems, bots, or scrapers to access, monitor, or extract content
          from the Site at scale.
        </li>
        <li>Do not disrupt or interfere with the Site's servers or networks.</li>
        <li>
          Do not impersonate any person or entity or falsely state or misrepresent your affiliation
          with a person or entity.
        </li>
        <li>Do not engage in any activity that could harm minors.</li>
      </ul>

      <h2>5. Editorial Content</h2>
      <p>
        Articles, match reports, and analysis published on the Site are journalistic works
        protected by copyright. While we aim for accuracy, football is a fast-moving subject, and
        results, line-ups, and quotes may be updated without notice. Where an article cites an
        external outlet, that outlet remains the authoritative source for its own reporting.
      </p>

      <h2>6. User Contributions</h2>
      <p>
        Where we provide functionality for you to submit feedback, comments, or other content
        ("User Contributions"), you agree that any such submissions are non-confidential and that
        we may use, modify, reproduce, and distribute them for editorial and operational purposes
        without further permission or compensation.
      </p>
      <p>
        You represent and warrant that you own or have the necessary rights to submit User
        Contributions and that they do not violate any laws or infringe upon any third-party
        rights.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        All content on the Site, including but not limited to articles, images, logos, and design
        elements, is the property of Weborbis Sport or its licensors and is protected by
        copyright, trademark, and other intellectual property laws. You may not reproduce,
        distribute, or modify any content from the Site without our prior written consent.
      </p>

      <h2>8. Third-Party Links</h2>
      <p>
        The Site may contain links to third-party websites or services. We have no control over,
        and assume no responsibility for, the content, privacy policies, or practices of any
        third-party websites or services.
      </p>

      <h2>9. Disclaimer of Warranties</h2>
      <p>
        THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WEBORBIS SPORT MAKES NO
        WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, WEBORBIS SPORT SHALL NOT BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR IN
        CONNECTION WITH YOUR USE OF THE SITE.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold Weborbis Sport harmless from and against any and
        all claims, damages, obligations, losses, liabilities, costs, and expenses (including
        attorney's fees) arising out of or in connection with your use of the Site or breach of
        this Agreement.
      </p>

      <h2>12. Termination</h2>
      <p>
        We reserve the right to suspend or terminate your access to the Site immediately, without
        prior notice or liability, for any reason whatsoever, including without limitation if you
        breach this Agreement.
      </p>
      <p>
        Upon termination, all provisions of this Agreement which by their nature should survive
        termination shall survive, including ownership provisions, warranty disclaimers,
        indemnity, and limitations of liability.
      </p>

      <h2>13. Changes to This Agreement</h2>
      <p>
        We reserve the right to modify or replace this Agreement at any time. We will provide at
        least 30 days' notice prior to any new terms taking effect. Your continued use of the Site
        after the effective date of the revised Agreement constitutes acceptance of the revised
        terms.
      </p>

      <h2>14. Governing Law and Jurisdiction</h2>
      <p>
        This Agreement shall be governed and construed in accordance with the laws of the State of
        Delaware, United States, without regard to its conflict of law principles.
      </p>
      <p>
        You agree that any dispute arising out of or relating to this Agreement or the Site shall
        be resolved exclusively in the state or federal courts located in New Castle County,
        Delaware, United States.
      </p>

      <h2>15. Entire Agreement</h2>
      <p>
        This Agreement, together with our Terms of Service and Privacy Policy, constitutes the
        entire agreement between Weborbis Sport and you regarding the Site.
      </p>

      <h2>16. Contact Us</h2>
      <p>
        For questions about this Agreement, please contact us at{" "}
        <strong>legal@weborbis.top</strong>.
      </p>
    </LegalPage>
  );
}
